import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis } from 'recharts';
import { supabase } from '@/integrations/supabase/client';
import { TrendingUp, BarChart3 } from 'lucide-react';
import { calculateOverallMaturity } from '@/utils/maturityLevelUtils';

interface ComparisonData {
  category: string;
  freeCurrent: number;
  premiumCurrent: number;
}

interface MaturityComparison {
  freeScore: number;
  freeLabel: string;
  premiumScore: number;
  premiumLabel: string;
}

export default function AssessmentComparisonChart() {
  const [comparisonData, setComparisonData] = useState<ComparisonData[]>([]);
  const [maturityComparison, setMaturityComparison] = useState<MaturityComparison | null>(null);
  const [loading, setLoading] = useState(true);
  const [hasData, setHasData] = useState(false);

  useEffect(() => {
    fetchComparisonData();
  }, []);

  const fetchComparisonData = async () => {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { setLoading(false); return; }

      const { data: myResponses } = await supabase.functions.invoke('get-my-responses');
      const normalizedResponses = Array.isArray(myResponses) ? myResponses : [];
      const freeResponses = normalizedResponses.filter((response: any) => !response.userId);
      const premiumResponses = normalizedResponses.filter((response: any) => Boolean(response.userId));

      if (!freeResponses?.length && !premiumResponses?.length) {
        setLoading(false);
        return;
      }

      // Find matching assessment IDs (same assessment taken as free + premium)
      const freeByAssessment = new Map<string, any>();
      (freeResponses || []).forEach((r: any) => {
        // Only take the latest free response per assessment
        if (!freeByAssessment.has(r.assessmentId)) {
          freeByAssessment.set(r.assessmentId, r);
        }
      });

      const premiumByAssessment = new Map<string, any>();
      (premiumResponses || []).forEach((r: any) => {
        if (!premiumByAssessment.has(r.assessmentId)) {
          premiumByAssessment.set(r.assessmentId, r);
        }
      });

      // Find assessments that exist in both free and premium
      let matchedAssessmentId: string | null = null;
      for (const assessmentId of freeByAssessment.keys()) {
        if (premiumByAssessment.has(assessmentId)) {
          matchedAssessmentId = assessmentId;
          break;
        }
      }

      if (!matchedAssessmentId) {
        // No matching assessment found - try to show latest of each anyway
        // Use the latest free and latest premium even if different assessments
        const latestFree = freeResponses?.[0];
        const latestPremium = premiumResponses?.[0];

        if (latestFree && latestPremium) {
          // Different assessments - still compare for general improvement view
          matchedAssessmentId = latestFree.assessmentId;
          // Use free response data and premium from different assessment
          await buildComparisonFromResponses(latestFree, latestPremium, true);
        } else {
          setLoading(false);
          return;
        }
      } else {
        const freeResponse = freeByAssessment.get(matchedAssessmentId);
        const premiumResponse = premiumByAssessment.get(matchedAssessmentId);
        await buildComparisonFromResponses(freeResponse, premiumResponse, false);
      }
    } catch (err) {
      console.error('Error fetching comparison data:', err);
    } finally {
      setLoading(false);
    }
  };

  const buildComparisonFromResponses = async (freeResponse: any, premiumResponse: any, _isDifferentAssessment: boolean) => {
    const freeScores = freeResponse.scoresByCategory || {};
    let premiumScores = premiumResponse.scoresByCategory || premiumResponse.scores_by_category || {};
    if (typeof premiumScores === 'string') premiumScores = JSON.parse(premiumScores);

    // Get category names - collect all category IDs
    const allCategoryIds = new Set<string>([
      ...Object.keys(freeScores),
      ...Object.keys(premiumScores),
    ]);

    // Fetch category names
    const { data: categories } = await supabase
      .from('categories')
      .select('id, name')
      .in('id', Array.from(allCategoryIds));

    const categoryNameMap = new Map<string, string>();
    (categories || []).forEach(c => categoryNameMap.set(c.id, c.name));

    // Also check if scores have embedded category names
    Object.entries(freeScores).forEach(([id, s]: [string, any]) => {
      if (s.categoryName && !categoryNameMap.has(id)) {
        categoryNameMap.set(id, s.categoryName);
      }
    });

    // Build comparison data for categories present in both
    const commonCategories = [...allCategoryIds].filter(
      id => freeScores[id] && premiumScores[id]
    );

    if (commonCategories.length === 0) {
      return;
    }

    const data: ComparisonData[] = commonCategories.map(catId => ({
      category: categoryNameMap.get(catId) || catId.slice(0, 8),
      freeCurrent: freeScores[catId]?.current || 0,
      premiumCurrent: premiumScores[catId]?.current || 0,
    }));

    setComparisonData(data);
    setHasData(true);

    // Calculate maturity comparison
    const freeClean: Record<string, { current: number; target: number }> = {};
    Object.entries(freeScores).forEach(([id, s]: [string, any]) => {
      freeClean[id] = { current: s.current || 0, target: s.target || 0 };
    });
    const premiumClean: Record<string, { current: number; target: number }> = {};
    Object.entries(premiumScores).forEach(([id, s]: [string, any]) => {
      premiumClean[id] = { current: s.current || 0, target: s.target || 0 };
    });

    const freeMaturity = calculateOverallMaturity(freeClean);
    const premiumMaturity = calculateOverallMaturity(premiumClean);

    if (freeMaturity && premiumMaturity) {
      setMaturityComparison({
        freeScore: freeMaturity.current?.score || 0,
        freeLabel: freeMaturity.current?.label || 'N/A',
        premiumScore: premiumMaturity.current?.score || 0,
        premiumLabel: premiumMaturity.current?.label || 'N/A',
      });
    }
  };

  const improvement = useMemo(() => {
    if (comparisonData.length === 0) return 0;
    const avgFree = comparisonData.reduce((sum, d) => sum + d.freeCurrent, 0) / comparisonData.length;
    const avgPremium = comparisonData.reduce((sum, d) => sum + d.premiumCurrent, 0) / comparisonData.length;
    return avgPremium - avgFree;
  }, [comparisonData]);

  if (loading) {
    return null; // Don't show loading state to avoid layout shift
  }

  if (!hasData) {
    return null; // Don't render if no comparison data available
  }

  return (
    <Card className="border border-border mb-8">
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-primary/10 text-primary">
              <BarChart3 className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">Progress Overview</h2>
              <p className="text-sm text-muted-foreground">Free survey vs Premium assessment scores</p>
            </div>
          </div>
          {improvement !== 0 && (
            <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium ${
              improvement > 0 
                ? 'bg-green-50 text-green-700 border border-green-200' 
                : 'bg-amber-50 text-amber-700 border border-amber-200'
            }`}>
              <TrendingUp className={`h-4 w-4 ${improvement < 0 ? 'rotate-180' : ''}`} />
              {improvement > 0 ? '+' : ''}{improvement.toFixed(1)} avg
            </div>
          )}
        </div>

        {/* Maturity Score Comparison Cards */}
        {maturityComparison && (
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="rounded-xl border border-border bg-muted/30 p-4">
              <p className="text-xs text-muted-foreground mb-1">Free Survey</p>
              <p className="text-2xl font-bold text-foreground">{maturityComparison.freeScore.toFixed(1)}</p>
              <p className="text-xs text-muted-foreground">{maturityComparison.freeLabel}</p>
            </div>
            <div className="rounded-xl border border-primary/20 bg-primary/5 p-4">
              <p className="text-xs text-muted-foreground mb-1">Premium Assessment</p>
              <p className="text-2xl font-bold text-primary">{maturityComparison.premiumScore.toFixed(1)}</p>
              <p className="text-xs text-muted-foreground">{maturityComparison.premiumLabel}</p>
            </div>
          </div>
        )}

        {/* Bar Chart */}
        <div className="w-full h-[300px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={comparisonData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis 
                dataKey="category" 
                tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                axisLine={{ stroke: 'hsl(var(--border))' }}
              />
              <YAxis 
                domain={[0, 4]} 
                tickCount={5}
                tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                axisLine={{ stroke: 'hsl(var(--border))' }}
              />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'hsl(var(--card))',
                  border: '1px solid hsl(var(--border))',
                  borderRadius: '8px',
                  fontSize: '12px',
                }}
                labelStyle={{ color: 'hsl(var(--foreground))', fontWeight: 600 }}
              />
              <Legend 
                wrapperStyle={{ fontSize: '12px' }}
              />
              <Bar 
                dataKey="freeCurrent" 
                name="Free Survey" 
                fill="#8E9196" 
                radius={[4, 4, 0, 0]}
                maxBarSize={40}
              />
              <Bar 
                dataKey="premiumCurrent" 
                name="Premium Assessment" 
                fill="hsl(var(--primary))" 
                radius={[4, 4, 0, 0]}
                maxBarSize={40}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}
